/**
 * @file shipment-readiness.ts
 * @description Чек-лист «готовность к передаче в ТК» для сайдбара новой
 * отгрузки (phase19.24 шаг 5.2, замечание пользователя 2026-09-22).
 *
 * Зачем файл существует: СДЭК примет заявку и с телефоном-заглушкой, и без
 * габаритов — а курьер потом не дозвонится до клиента. Менеджер должен видеть
 * ДО создания отгрузки, чего не хватает, и понимать, что из этого блокирует
 * передачу, а что только ухудшает расчёт. Чистая функция без React: одна и та
 * же проверка у сайдбара и у тестов.
 *
 * Уровни: `fail` — перевозчик откажет или клиент недостижим, передача
 * блокируется; `warn` — заявка пройдёт, но с потерей точности.
 */

import type { OrderDeliveryDto } from "./api-orders";
import type { OrderDeliveryReceiptDto } from "./api-delivery-agreement";
import type { DeliveryPackageDraft } from "./delivery-quote";
import { toNumber } from "./delivery-quote";

export type ReadinessLevel = "ok" | "warn" | "fail";

export interface ReadinessCheck {
  key: "recipient" | "destination" | "tariff" | "packages" | "items";
  level: ReadinessLevel;
  /** Короткая подпись чипа: «получатель», «ПВЗ». */
  label: string;
  /** Что именно не так и что сделать; `null` — всё в порядке. */
  hint: string | null;
}

export interface ReadinessInput {
  delivery: OrderDeliveryDto | null;
  receipt: OrderDeliveryReceiptDto | null;
  packages: DeliveryPackageDraft[];
}

/** Телефон годится для перевозчика: хотя бы 10 цифр. */
function hasPhone(phone: string | null | undefined): boolean {
  return (phone ?? "").replace(/\D/gu, "").length >= 10;
}

export function shipmentReadiness(input: ReadinessInput): ReadinessCheck[] {
  const { delivery, receipt, packages } = input;
  const recipient = delivery?.recipient ?? null;
  const checks: ReadinessCheck[] = [];

  const recipientOk = !!recipient?.name?.trim() && hasPhone(recipient?.phone);
  checks.push({
    key: "recipient",
    level: recipientOk ? "ok" : "fail",
    label: "получатель",
    hint: recipientOk
      ? null
      : !recipient?.name?.trim()
        ? "У клиента не заполнено имя — заявка уйдёт на «Получатель». Заполните в карточке клиента."
        : "У клиента нет телефона — курьер и ПВЗ не смогут связаться. Заполните в карточке клиента.",
  });

  const pvz = receipt?.quote?.destination.pickupPointCode ?? receipt?.pickupPointCode ?? null;
  const address = receipt?.address?.trim() ?? "";
  const destinationOk = receipt?.method === "SHOP_COURIER" ? address !== "" : !!pvz || address !== "";
  checks.push({
    key: "destination",
    level: destinationOk ? "ok" : "fail",
    label: pvz ? "ПВЗ" : "адрес",
    hint: destinationOk ? null : "Не указан ни пункт выдачи, ни адрес — согласуйте условия во вкладке «Доставка».",
  });

  if (receipt?.method === "CARRIER") {
    const tariffOk = !!receipt.quote;
    checks.push({
      key: "tariff",
      level: tariffOk ? "ok" : "warn",
      label: "тариф",
      hint: tariffOk
        ? null
        : "Тариф не рассчитан — перевозчик назначит его сам, стоимость может разойтись с согласованной.",
    });
  }

  const weights = packages.map((p) => toNumber(p.weightG) ?? 0);
  const weightOk = packages.length > 0 && weights.every((w) => w > 0);
  const dimsOk = packages.every(
    (p) => (toNumber(p.lengthMm) ?? 0) > 0 && (toNumber(p.widthMm) ?? 0) > 0 && (toNumber(p.heightMm) ?? 0) > 0,
  );
  checks.push({
    key: "packages",
    level: !weightOk ? "fail" : dimsOk ? "ok" : "warn",
    label: dimsOk ? "места" : "габариты",
    hint: !weightOk
      ? "У места не указан вес — без него перевозчик не примет заявку."
      : dimsOk
        ? null
        : "Габариты мест не заданы — СДЭК посчитает по своим нормативам, тариф может вырасти.",
  });

  const lines = delivery?.items ?? [];
  const noWeight = lines.filter((l) => !l.weightG || l.weightG <= 0);
  checks.push({
    key: "items",
    level: noWeight.length === 0 ? "ok" : "warn",
    label: "состав",
    hint:
      noWeight.length === 0
        ? null
        : `Без веса в карточке товара: ${noWeight.map((l) => l.name).join(", ")} — вес места проверьте вручную.`,
  });

  return checks;
}

/** Есть ли блокирующие пункты — «Создать отгрузку» остаётся, «Передать в ТК» ждёт. */
export function readinessBlocked(checks: ReadinessCheck[]): boolean {
  return checks.some((c) => c.level === "fail");
}
